import React, { useEffect, useState } from 'react';
import { useAPI } from '../contexts/APIContext';
import logger from '../utils/logger';

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const ReferralStatsDisplay = () => {
  const { apiClient } = useAPI();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.getReferralStats();
        if (response.status) {
          setStats(response.data);
        } else {
          setError(response.error || 'Failed to load referral stats');
        }
      } catch (err) {
        setError('Failed to load referral stats');
        logger.error('Error fetching referral stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [apiClient]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div style={{ width: 28, height: 28, borderRadius: "50%", border: "3px solid rgba(0,101,248,0.20)", borderTopColor: "#0065F8", animation: "ref-spin 1s linear infinite" }} />
        <span style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, marginLeft: 12, fontWeight: 600 }}>Loading referral stats…</span>
        <style>{`@keyframes ref-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          background: "#FFFFFF",
          border: "2px solid rgba(220,38,38,0.25)",
          borderRadius: 18,
          padding: 20,
          boxShadow: "0px 3px 0px rgba(11,26,51,0.05)",
        }}
      >
        <p style={{ ...FREDOKA, color: "#B91C1C", fontSize: 14, fontWeight: 600, margin: 0 }}>{error}</p>
      </div>
    );
  }

  if (!stats) return null;

  const starsPerReferral = 125;
  const progressPercentage = stats.total_allowed > 0
    ? Math.min((stats.completed / stats.total_allowed) * 100, 100)
    : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Monthly Progress */}
      <div
        style={{
          background: "#FFFFFF",
          border: "2px solid rgba(11,26,51,0.06)",
          borderRadius: 22,
          padding: 24,
          boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 24px rgba(11,26,51,0.06)",
        }}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
              This Month
            </span>
            <h4 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 20, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "4px 0 4px" }}>
              Monthly Progress
            </h4>
            <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, margin: 0 }}>
              <strong style={{ color: "#0B1A33" }}>{stats.completed}</strong> of {stats.total_allowed} referrals
            </p>
          </div>
          <div style={{ ...FREDOKA, color: "#0065F8", fontWeight: 800, fontSize: 30, lineHeight: 1 }}>
            {progressPercentage.toFixed(0)}<span style={{ fontSize: 16, fontWeight: 700, color: "#5A6B85" }}>%</span>
          </div>
        </div>

        <div style={{ height: 12, background: "rgba(11,26,51,0.08)", borderRadius: 999, overflow: "hidden", marginBottom: 16 }}>
          <div
            style={{
              height: "100%",
              width: `${progressPercentage}%`,
              background: "linear-gradient(90deg, #0065F8 0%, #00CAFF 100%)",
              borderRadius: 999,
              boxShadow: "0px 0px 12px rgba(0,101,248,0.30)",
              transition: "width 500ms ease",
            }}
          />
        </div>

        {stats.limit_reached ? (
          <div
            className="flex items-center gap-2.5"
            style={{
              background: "rgba(220,38,38,0.06)",
              border: "1.5px solid rgba(220,38,38,0.20)",
              borderRadius: 14,
              padding: "10px 14px",
            }}
          >
            <svg width="18" height="18" fill="none" stroke="#DC2626" strokeWidth="2.2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <p style={{ ...FREDOKA, color: "#B91C1C", fontSize: 12, fontWeight: 600, margin: 0 }}>
              Monthly limit reached. Resets {stats.reset_date || "the 1st of next month"}.
            </p>
          </div>
        ) : stats.remaining > 0 ? (
          <div
            className="flex items-center gap-2.5"
            style={{
              background: "rgba(0,101,248,0.06)",
              border: "1.5px solid rgba(0,101,248,0.18)",
              borderRadius: 14,
              padding: "10px 14px",
            }}
          >
            <svg width="18" height="18" fill="none" stroke="#0065F8" strokeWidth="2.2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
            <p style={{ ...FREDOKA, color: "#0B1A33", fontSize: 12, fontWeight: 600, margin: 0 }}>
              Earn <strong style={{ color: "#0065F8" }}>{stats.remaining * starsPerReferral}</strong> more stars from {stats.remaining} more referral{stats.remaining !== 1 ? "s" : ""}.
            </p>
          </div>
        ) : null}
      </div>

      {/* Lifetime Stats — Pixley blue gradient hero */}
      <div
        className="relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)",
          borderRadius: 22,
          padding: 24,
          color: "#FFFFFF",
          boxShadow: "0px 5px 0px rgba(7,59,142,0.35), 0px 18px 32px rgba(0,101,248,0.22)",
        }}
      >
        <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.10)", filter: "blur(20px)" }} />
        <div style={{ position: "absolute", top: 14, right: 16, opacity: 0.18 }}>
          <svg width="80" height="80" fill="#FFFFFF" viewBox="0 0 24 24">
            <path d="M12 2l2.39 4.84L20 7.27l-4 3.91.94 5.5L12 14.77l-4.94 2.92.94-5.5-4-3.91 5.61-.43L12 2z" />
          </svg>
        </div>

        <div className="relative z-10">
          <span style={{ ...FREDOKA, background: "rgba(255,255,255,0.20)", color: "#FFFFFF", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", padding: "5px 12px", borderRadius: 999, border: "1.5px solid rgba(255,255,255,0.30)", display: "inline-block" }}>
            All Time
          </span>
          <h4 style={{ ...FREDOKA, fontWeight: 700, fontSize: 20, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "10px 0 14px" }}>
            Lifetime Stats
          </h4>

          <div className="space-y-2.5">
            <div
              className="flex items-center justify-between"
              style={{
                background: "rgba(255,255,255,0.14)",
                border: "1.5px solid rgba(255,255,255,0.22)",
                borderRadius: 14,
                padding: "12px 14px",
              }}
            >
              <div className="flex items-center gap-3">
                <svg width="20" height="20" fill="none" stroke="#FFFFFF" strokeWidth="2.4" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l3 3 7-7" />
                </svg>
                <span style={{ ...FREDOKA, color: "rgba(255,255,255,0.95)", fontWeight: 600, fontSize: 14 }}>Total Referrals</span>
              </div>
              <span style={{ ...FREDOKA, color: "#FFFFFF", fontWeight: 800, fontSize: 22, lineHeight: 1 }}>{stats.lifetime_completed ?? 0}</span>
            </div>
            <div
              className="flex items-center justify-between"
              style={{
                background: "rgba(255,255,255,0.14)",
                border: "1.5px solid rgba(255,255,255,0.22)",
                borderRadius: 14,
                padding: "12px 14px",
              }}
            >
              <div className="flex items-center gap-3">
                <img src="/assets/pixley_stars.png" alt="" style={{ width: 20, height: 20, filter: "brightness(0) invert(1)" }} />
                <span style={{ ...FREDOKA, color: "rgba(255,255,255,0.95)", fontWeight: 600, fontSize: 14 }}>Stars Earned</span>
              </div>
              <span style={{ ...FREDOKA, color: "#FFFFFF", fontWeight: 800, fontSize: 22, lineHeight: 1 }}>{stats.lifetime_stars_earned ?? 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralStatsDisplay;
